/*
  Merchant 2.0 AI Dashboard — STEP 12
  Replaces the legacy /biz demo. Four modules:
    1. ZeroLegalPage         — ZK-KYB vault + AI Corporate Lawyer + Agreement Lock
    2. PayrollPage           — Turbo-Zarplata bulk roster + HR analytics + payout
    3. AcquiringPage2        — Multi-terminal + Smart Tax + Auto documents
    4. SupplyChainPage       — Catalog + AI inventory + B2B Purchase Orders

  Layout follows the same Page/Card conventions as user.jsx / business.jsx.
  All state syncs live with /api/tgc/merchant/* every 15s.
*/
import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../api";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import {
  ShieldCheck, FileText, Lock, UploadCloud, ScanLine, Users, Cpu, PlayCircle,
  Store, Receipt, Landmark, Boxes, PackageCheck, TruckIcon, RefreshCw, Send,
  CheckCircle2, AlertCircle, Sparkles, GraduationCap, Zap, Building2,
} from "lucide-react";
import { FiatStabilityBanner } from "./CorporateBanking";

/* ══════════════════ helpers ══════════════════ */
const Page = ({ title, sub, icon, children, testId }) => (
  <div data-testid={testId}>
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
      {icon && <div style={{ color: "#00F2FF" }}>{icon}</div>}
      <h2 className="page-title" style={{ margin: 0 }}>{title}</h2>
    </div>
    {sub && <p className="page-sub" style={{ marginTop: 6 }}>{sub}</p>}
    {children}
  </div>
);
const Card = ({ children, style = {}, testId, ...p }) => (
  <div className="tg-card" data-testid={testId} style={{ padding: 22, ...style }} {...p}>{children}</div>
);
const KPI = ({ label, value, color = "#00F2FF", icon, sub, testId }) => (
  <div className="tg-card" data-testid={testId} style={{ padding: 18, background: `linear-gradient(135deg, ${color}12, rgba(255,255,255,0.02))`, borderColor: `${color}30` }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, color }}>
      {icon}
      <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, opacity: 0.85 }}>{label}</span>
    </div>
    <div style={{ fontSize: 28, fontWeight: 900, marginTop: 8, fontFeatureSettings: '"tnum"' }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: "#8B94B8", marginTop: 4 }}>{sub}</div>}
  </div>
);
const fmt = (n, d = 2) => Number(n || 0).toFixed(d);
const Btn = ({ children, onClick, variant = "primary", testId, disabled, style = {} }) => {
  const base = { padding: "10px 16px", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", border: "none", opacity: disabled ? 0.55 : 1, display: "inline-flex", alignItems: "center", gap: 6 };
  const styles = {
    primary:   { ...base, background: "linear-gradient(135deg, #00F2FF, #7C3AED)", color: "#050914" },
    ghost:     { ...base, background: "transparent", border: "1px solid rgba(0,242,255,0.35)", color: "#00F2FF" },
    danger:    { ...base, background: "#FF3D6C", color: "#fff" },
    warning:   { ...base, background: "#FFD700", color: "#050914" },
    enterprise:{ ...base, background: "linear-gradient(135deg, #BC13FE, #FF3D6C)", color: "#fff" },
  };
  return <button data-testid={testId} onClick={onClick} disabled={disabled} style={{ ...styles[variant], ...style }}>{children}</button>;
};

/* ══════════════════ 1. ZERO-LEGAL AI COMPLIANCE ══════════════════ */
export function ZeroLegalPage() {
  const [overview, setOverview] = useState(null);
  const [docs, setDocs] = useState([]);
  const [audits, setAudits] = useState([]);
  const [agreements, setAgreements] = useState({ matrix: [], complete: false });
  const [busy, setBusy] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [contractText, setContractText] = useState("");
  const [counterparty, setCounterparty] = useState("");
  const [wallet, setWallet] = useState(localStorage.getItem("tgc_fiat_user_id") ? "0xmerch" + Date.now().toString(16) : "");

  const reload = async () => {
    try {
      const [o, d, a, ag] = await Promise.all([
        api.get("/merchant/overview"),
        api.get("/merchant/kyb/list"),
        api.get("/merchant/legal/audits"),
        api.get("/merchant/agreements/status"),
      ]);
      setOverview(o.data);
      setDocs(d.data.docs || []);
      setAudits(a.data.audits || []);
      setAgreements(ag.data);
    } catch (_) { /* noop */ }
  };
  useEffect(() => { reload(); }, []);
  useAutoRefresh(reload, 15000);

  const uploadDoc = async () => {
    if (!selectedFile) return;
    setBusy(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(",")[1] || "");
        r.onerror = () => reject(r.error);
        r.readAsDataURL(selectedFile);
      });
      await api.post("/merchant/kyb/upload", {
        doc_type: "incorporation", filename: selectedFile.name, content_b64: b64, mime_type: selectedFile.type || "application/pdf",
      });
      setSelectedFile(null);
      await reload();
    } catch (e) { alert("Upload failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const runAudit = async () => {
    if (contractText.trim().length < 20) { alert("Paste at least 20 chars of contract text"); return; }
    setBusy(true);
    try {
      await api.post("/merchant/legal/audit", { contract_text: contractText, counterparty: counterparty || null });
      setContractText(""); setCounterparty("");
      await reload();
    } catch (e) { alert("Audit failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const lockAgreement = async (key) => {
    if (!wallet || wallet.length < 8) { alert("Enter your Web3 wallet key first"); return; }
    setBusy(true);
    try {
      await api.post("/merchant/agreements/lock", { agreement_key: key, wallet_address: wallet });
      await reload();
    } catch (e) { alert("Lock failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  return (
    <Page title="ZERO-LEGAL — AI Compliance Vault"
      sub="ZK-KYB safe · AI Corporate Lawyer · Automated Agreement Locking"
      icon={<ShieldCheck size={26} />}
      testId="merchant-zerolegal-page">
      <div className="dash-grid dash-grid-4" style={{ marginBottom: 22 }}>
        <KPI testId="kpi-kyb-docs"    label="KYB Docs"          value={overview?.compliance?.kyb_docs ?? "—"}          color="#00F2FF" icon={<UploadCloud size={16} />} sub="Encrypted commitments" />
        <KPI testId="kpi-audits"       label="AI Legal Audits"   value={overview?.compliance?.legal_audits ?? "—"}      color="#FFD700" icon={<ScanLine size={16} />}   sub="Risk scores generated" />
        <KPI testId="kpi-agreements"   label="Agreements Locked" value={`${overview?.compliance?.agreements_locked ?? 0}/${overview?.compliance?.agreements_required ?? 4}`} color="#10F4A7" icon={<Lock size={16} />} sub="Web3-signed" />
        <KPI testId="kpi-compliance"   label="Compliance Score"  value={`${Math.round(((overview?.compliance?.agreements_locked || 0) / (overview?.compliance?.agreements_required || 4)) * 100)}%`} color={agreements.complete ? "#10F4A7" : "#FF9F43"} icon={<Sparkles size={16} />} sub={agreements.complete ? "Ready to activate" : "In progress"} />
      </div>

      <div className="dash-grid dash-grid-2" style={{ gap: 20, marginBottom: 20 }}>
        {/* ZK-KYB Vault */}
        <Card testId="zerolegal-kyb-panel">
          <h3 style={{ margin: "0 0 4px", fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
            <UploadCloud size={16} color="#00F2FF" /> ZK-KYB Encrypted Vault
          </h3>
          <p style={{ fontSize: 12, color: "#8B94B8", marginTop: 4 }}>
            Drop incorporation files, licences, board resolutions. Server stores a SHA-256 commitment only — plaintext never leaves the client.
          </p>
          <input type="file" data-testid="kyb-file-input" accept=".pdf,.png,.jpg,.jpeg,.xml,.docx"
            onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            style={{ width: "100%", padding: 10, border: "1px dashed rgba(0,242,255,0.35)", borderRadius: 10, background: "rgba(0,242,255,0.03)", color: "#E7ECFF", marginBottom: 10 }} />
          <Btn testId="kyb-upload-btn" onClick={uploadDoc} disabled={!selectedFile || busy}>
            <UploadCloud size={14} /> Upload to ZK-Vault
          </Btn>
          <div data-testid="kyb-docs-table" style={{ marginTop: 16, maxHeight: 220, overflowY: "auto" }}>
            {docs.length === 0 && <div style={{ fontSize: 12, color: "#5F6884", padding: 12 }}>No documents uploaded yet.</div>}
            {docs.map((d) => (
              <div key={d.doc_id} style={{ padding: "8px 10px", display: "flex", justifyContent: "space-between", fontSize: 12, borderBottom: "1px dashed rgba(255,255,255,0.06)" }}>
                <div>
                  <b>{d.filename}</b>
                  <div style={{ color: "#5F6884", fontSize: 10 }}>{d.doc_type} · <code>{d.commitment_sha256}</code></div>
                </div>
                <span style={{ padding: "2px 8px", borderRadius: 999, fontSize: 10, background: d.status.includes("SAFE") ? "#10F4A733" : d.status.includes("BLOCK") ? "#FF3D6C33" : "#FFD70033", color: d.status.includes("SAFE") ? "#10F4A7" : d.status.includes("BLOCK") ? "#FF3D6C" : "#FFD700" }}>
                  {d.status}
                </span>
              </div>
            ))}
          </div>
        </Card>

        {/* AI Legal Audit */}
        <Card testId="zerolegal-audit-panel">
          <h3 style={{ margin: "0 0 4px", fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
            <Cpu size={16} color="#BC13FE" /> AI Corporate Lawyer
          </h3>
          <p style={{ fontSize: 12, color: "#8B94B8", marginTop: 4 }}>
            Paste any B2B contract clause. The engine scans for hidden penalties, indemnities, auto-renewals, exclusivity locks and outputs an executive summary.
          </p>
          <input placeholder="Counterparty (optional)" value={counterparty} onChange={(e) => setCounterparty(e.target.value)}
            data-testid="audit-counterparty"
            style={{ width: "100%", padding: 10, marginBottom: 8, border: "1px solid rgba(188,19,254,0.35)", borderRadius: 10, background: "rgba(188,19,254,0.03)", color: "#E7ECFF" }} />
          <textarea data-testid="audit-contract-text" value={contractText}
            onChange={(e) => setContractText(e.target.value)}
            placeholder="Paste the contract text here (min. 20 chars)…"
            style={{ width: "100%", height: 120, padding: 10, border: "1px solid rgba(188,19,254,0.35)", borderRadius: 10, background: "rgba(188,19,254,0.03)", color: "#E7ECFF", fontFamily: "monospace", fontSize: 12 }} />
          <Btn testId="audit-run-btn" onClick={runAudit} disabled={busy} variant="enterprise" style={{ marginTop: 8 }}>
            <ScanLine size={14} /> Run AI Audit
          </Btn>
          <div data-testid="audit-history" style={{ marginTop: 14, maxHeight: 200, overflowY: "auto" }}>
            {audits.slice(0, 5).map((a) => (
              <div key={a.audit_id} style={{ padding: 10, marginBottom: 8, borderRadius: 8, background: "rgba(0,0,0,0.28)", border: `1px solid ${a.verdict === "BLOCK" ? "#FF3D6C" : a.verdict === "SAFE_TO_SIGN" ? "#10F4A7" : "#FFD700"}30` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <b style={{ fontSize: 12, color: a.verdict === "BLOCK" ? "#FF3D6C" : a.verdict === "SAFE_TO_SIGN" ? "#10F4A7" : "#FFD700" }}>{a.verdict}</b>
                  <span style={{ fontSize: 10, color: "#8B94B8" }}>risk={a.risk_score}/100 · {a.findings?.length || 0} findings</span>
                </div>
                <div style={{ fontSize: 11, color: "#8B94B8", marginTop: 4, whiteSpace: "pre-line" }}>{a.executive_summary}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Agreement lock */}
      <Card testId="zerolegal-agreements-panel">
        <h3 style={{ margin: "0 0 6px", fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
          <Lock size={16} color="#10F4A7" /> Automated Agreement Locking — Web3 Signed
        </h3>
        <p style={{ fontSize: 12, color: "#8B94B8", marginBottom: 12 }}>
          Sign & lock the four active compliance frameworks with your merchant Web3 wallet key. Each signature is a SHA-256 commitment of (merchant_id | wallet | key | timestamp).
        </p>
        <input placeholder="Web3 wallet address (0x…)" value={wallet} onChange={(e) => setWallet(e.target.value)}
          data-testid="agreement-wallet-input"
          style={{ width: "100%", padding: 10, marginBottom: 10, border: "1px solid rgba(16,244,167,0.35)", borderRadius: 10, background: "rgba(16,244,167,0.03)", color: "#E7ECFF", fontFamily: "monospace" }} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
          {agreements.matrix.map((m) => (
            <div key={m.agreement_key} data-testid={`agreement-${m.agreement_key}`}
              style={{ padding: 12, borderRadius: 10, border: `1px solid ${m.locked ? "#10F4A7" : "rgba(255,255,255,0.08)"}`, background: m.locked ? "rgba(16,244,167,0.06)" : "rgba(0,0,0,0.2)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <b style={{ fontSize: 13 }}>{m.title}</b>
                {m.locked ? <CheckCircle2 size={16} color="#10F4A7" /> : <AlertCircle size={16} color="#FF9F43" />}
              </div>
              <div style={{ fontSize: 10, color: "#5F6884", marginTop: 4 }}>v{m.version}{m.signature_hash ? ` · sig=${m.signature_hash}` : ""}</div>
              {!m.locked && (
                <Btn variant="ghost" style={{ marginTop: 10 }} onClick={() => lockAgreement(m.agreement_key)} disabled={busy}
                  testId={`lock-${m.agreement_key}`}>
                  <Lock size={12} /> Sign & Lock
                </Btn>
              )}
            </div>
          ))}
        </div>
      </Card>
    </Page>
  );
}

/* ══════════════════ 2. TURBO-ZARPLATA PAYROLL ══════════════════ */
export function PayrollPage() {
  const [overview, setOverview] = useState(null);
  const [employees, setEmployees] = useState({ employees: [], total: 0, by_role: {}, monthly_payroll_usdt: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [history, setHistory] = useState([]);
  const [q, setQ] = useState(""); const [roleFilter, setRoleFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [payoutResult, setPayoutResult] = useState(null);
  const fileRef = useRef(null);
  const [addForm, setAddForm] = useState({ full_name: "", email: "", role: "cashier", department: "", monthly_salary_usdt: 0 });

  const reload = async () => {
    try {
      const [o, e, a, h] = await Promise.all([
        api.get("/merchant/overview"),
        api.get(`/merchant/employees?q=${encodeURIComponent(q)}&role=${roleFilter}`),
        api.get("/merchant/hr/analytics").catch(() => ({ data: null })),
        api.get("/merchant/payroll/history"),
      ]);
      setOverview(o.data); setEmployees(e.data); setAnalytics(a.data); setHistory(h.data.runs || []);
    } catch (_) { /* noop */ }
  };
  useEffect(() => { reload(); }, [q, roleFilter]);
  useAutoRefresh(reload, 20000);

  const doImport = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/merchant/employees/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      alert(`Imported ${r.data.inserted} · updated ${r.data.updated} · skipped ${r.data.skipped}`);
      fileRef.current.value = "";
      await reload();
    } catch (e) { alert("Import failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const doAdd = async () => {
    if (!addForm.full_name) { alert("Full name required"); return; }
    setBusy(true);
    try {
      await api.post("/merchant/employees/add", addForm);
      setAddForm({ full_name: "", email: "", role: "cashier", department: "", monthly_salary_usdt: 0 });
      await reload();
    } catch (e) { alert("Add failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const doAssign = async (employee_id, role) => {
    try { await api.post("/merchant/employees/assign-role", { employee_id, role }); await reload(); }
    catch (e) { alert("Role assign failed: " + (e?.response?.data?.detail || e.message)); }
  };
  const doPayroll = async (dry_run) => {
    setBusy(true);
    try {
      const r = await api.post("/merchant/payroll/execute", { dry_run, include_kpi_bonus: true });
      setPayoutResult(r.data.run);
      await reload();
    } catch (e) { alert("Payroll failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const downloadTemplate = () => {
    const csv = "full_name,email,role,department,monthly_salary_usdt\nJohn Doe,john@example.com,cashier,Retail-Berlin,2500\nJane Smith,jane@example.com,finance,HQ,4500\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "employee_roster_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const roles = ["owner", "finance", "support", "developer", "viewer", "cashier"];

  return (
    <Page title="TURBO-ZARPLATA — Payroll Engine"
      sub="10 000+ roster CSV/XLSX import · RBAC · AI HR analytics · One-click bulk payout"
      icon={<Users size={26} />}
      testId="merchant-payroll-page">
      <FiatStabilityBanner compact />
      <div className="dash-grid dash-grid-4" style={{ marginBottom: 22 }}>
        <KPI testId="kpi-headcount"      label="Headcount"                 value={employees.total ?? 0}                                color="#00F2FF" icon={<Users size={16} />} sub={`across ${Object.keys(employees.by_role || {}).length} roles`} />
        <KPI testId="kpi-monthly-payroll" label="Monthly Payroll"          value={`$${fmt(employees.monthly_payroll_usdt)}`}            color="#FFD700" icon={<Landmark size={16} />} sub="USDT · gross" />
        <KPI testId="kpi-runs"           label="Payroll Runs"              value={overview?.payroll?.runs_executed ?? 0}                color="#10F4A7" icon={<PlayCircle size={16} />} sub="lifetime" />
        <KPI testId="kpi-avg-kpi"        label="Avg KPI"                   value={analytics?.by_department?.length ? `${fmt(analytics.by_department.reduce((s, d) => s + d.avg_kpi, 0) / analytics.by_department.length, 1)}` : "—"} color="#BC13FE" icon={<Sparkles size={16} />} sub={`${analytics?.top_performers?.length || 0} top performers`} />
      </div>

      <div className="dash-grid dash-grid-2" style={{ gap: 20, marginBottom: 20 }}>
        {/* Roster import */}
        <Card testId="payroll-import-panel">
          <h3 style={{ margin: "0 0 4px", fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
            <UploadCloud size={16} color="#00F2FF" /> Bulk Roster Import (1C / SAP / Oracle)
          </h3>
          <p style={{ fontSize: 12, color: "#8B94B8", marginTop: 4 }}>
            CSV/XLSX up to 20 MB. Headers: full_name, email, role, department, monthly_salary_usdt.
          </p>
          <input ref={fileRef} type="file" accept=".csv,.xlsx" data-testid="payroll-file-input"
            style={{ width: "100%", padding: 10, marginBottom: 8, border: "1px dashed rgba(0,242,255,0.35)", borderRadius: 10, background: "rgba(0,242,255,0.03)", color: "#E7ECFF" }} />
          <div style={{ display: "flex", gap: 8 }}>
            <Btn testId="payroll-import-btn" onClick={doImport} disabled={busy}><UploadCloud size={14} /> Import Roster</Btn>
            <Btn testId="payroll-template-btn" variant="ghost" onClick={downloadTemplate}><FileText size={14} /> CSV Template</Btn>
          </div>
        </Card>

        {/* Single employee add */}
        <Card testId="payroll-add-panel">
          <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>Add Single Employee</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <input placeholder="Full name" value={addForm.full_name} onChange={(e) => setAddForm({ ...addForm, full_name: e.target.value })} data-testid="emp-add-name"
              style={{ padding: 10, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, background: "rgba(0,0,0,0.25)", color: "#E7ECFF" }} />
            <input placeholder="Email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} data-testid="emp-add-email"
              style={{ padding: 10, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, background: "rgba(0,0,0,0.25)", color: "#E7ECFF" }} />
            <select value={addForm.role} onChange={(e) => setAddForm({ ...addForm, role: e.target.value })} data-testid="emp-add-role"
              style={{ padding: 10, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, background: "rgba(0,0,0,0.25)", color: "#E7ECFF" }}>
              {roles.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <input placeholder="Department" value={addForm.department} onChange={(e) => setAddForm({ ...addForm, department: e.target.value })}
              style={{ padding: 10, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, background: "rgba(0,0,0,0.25)", color: "#E7ECFF" }} />
            <input placeholder="Salary USDT" type="number" value={addForm.monthly_salary_usdt} onChange={(e) => setAddForm({ ...addForm, monthly_salary_usdt: Number(e.target.value) })} data-testid="emp-add-salary"
              style={{ padding: 10, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, background: "rgba(0,0,0,0.25)", color: "#E7ECFF" }} />
            <Btn testId="emp-add-btn" onClick={doAdd} disabled={busy}>Add</Btn>
          </div>
        </Card>
      </div>

      {/* HR Analytics */}
      {analytics?.by_department?.length > 0 && (
        <Card style={{ marginBottom: 20 }} testId="payroll-analytics-panel">
          <h3 style={{ margin: "0 0 8px", fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
            <Cpu size={16} color="#BC13FE" /> AI HR Performance Auditor
          </h3>
          <table className="dash-table">
            <thead><tr><th>Department</th><th>Headcount</th><th>Avg KPI</th><th>Avg Checkout (s)</th><th style={{ textAlign: "right" }}>Payroll USDT</th></tr></thead>
            <tbody>
              {analytics.by_department.map((d) => (
                <tr key={d.department}>
                  <td><b>{d.department}</b></td>
                  <td>{d.headcount}</td>
                  <td style={{ color: d.avg_kpi >= 85 ? "#10F4A7" : d.avg_kpi >= 70 ? "#FFD700" : "#FF9F43", fontWeight: 700 }}>{d.avg_kpi}</td>
                  <td>{d.avg_checkout_s}</td>
                  <td style={{ textAlign: "right", fontFeatureSettings: '"tnum"' }}>${fmt(d.total_salary_usdt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Employee grid + role assign */}
      <Card style={{ marginBottom: 20 }} testId="payroll-employee-grid">
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          <input placeholder="Search employees…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="emp-search"
            style={{ flex: 1, minWidth: 200, padding: 10, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, background: "rgba(0,0,0,0.25)", color: "#E7ECFF" }} />
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="emp-role-filter"
            style={{ padding: 10, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, background: "rgba(0,0,0,0.25)", color: "#E7ECFF" }}>
            <option value="">All roles</option>
            {roles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <table className="dash-table">
          <thead><tr><th>Name</th><th>Role</th><th>Dept</th><th>KPI</th><th>Salary</th><th style={{ textAlign: "right" }}>Actions</th></tr></thead>
          <tbody>
            {employees.employees?.length === 0 && <tr><td colSpan={6} style={{ textAlign: "center", padding: 24, color: "#5F6884" }}>No employees yet — upload a roster or add manually.</td></tr>}
            {employees.employees?.slice(0, 30).map((e) => (
              <tr key={e.employee_id} data-testid={`emp-row-${e.employee_id}`}>
                <td><b>{e.full_name}</b><div style={{ fontSize: 10, color: "#5F6884" }}>{e.email || "—"}</div></td>
                <td>
                  <select value={e.role} onChange={(ev) => doAssign(e.employee_id, ev.target.value)} data-testid={`emp-role-${e.employee_id}`}
                    style={{ padding: 4, background: "rgba(0,0,0,0.35)", color: "#E7ECFF", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, fontSize: 11 }}>
                    {roles.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td>{e.department || "—"}</td>
                <td style={{ color: (e.kpi_score || 0) >= 85 ? "#10F4A7" : "#FFD700", fontWeight: 700 }}>{e.kpi_score}</td>
                <td>${fmt(e.monthly_salary_usdt)}</td>
                <td style={{ textAlign: "right", fontSize: 10, color: "#5F6884" }}>{new Date(e.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Payroll execute */}
      <Card testId="payroll-execute-panel" style={{ background: "linear-gradient(135deg, rgba(188,19,254,0.10), rgba(255,215,0,0.05))", borderColor: "#BC13FE55" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, color: "#BC13FE" }}>Execute Bulk Payroll</h3>
            <div style={{ fontSize: 12, color: "#8B94B8", marginTop: 4 }}>
              {employees.total} employees · ${fmt(employees.monthly_payroll_usdt)} gross → instant Cash Wallet credits (KPI ≥ 85 gets +10 % bonus).
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn testId="payroll-dryrun-btn" variant="ghost" onClick={() => doPayroll(true)} disabled={busy || employees.total === 0}>Dry Run</Btn>
            <Btn testId="payroll-execute-btn" variant="enterprise" onClick={() => doPayroll(false)} disabled={busy || employees.total === 0}>
              <PlayCircle size={14} /> Execute Bulk Payroll
            </Btn>
          </div>
        </div>
        {payoutResult && (
          <div data-testid="payroll-result" style={{ marginTop: 14, padding: 12, borderRadius: 10, background: "rgba(16,244,167,0.06)", border: "1px solid #10F4A755" }}>
            <b>Run {payoutResult.run_id}</b> · status <span style={{ color: "#10F4A7" }}>{payoutResult.status}</span>
            <div style={{ fontSize: 12, marginTop: 4 }}>Paid {payoutResult.employees_paid} employees · Total debited ${fmt(payoutResult.total_debit_usdt)}</div>
          </div>
        )}
        {history.length > 0 && (
          <div style={{ marginTop: 14, maxHeight: 160, overflowY: "auto" }}>
            {history.map((h) => (
              <div key={h.run_id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 4px", borderBottom: "1px dashed rgba(255,255,255,0.06)", fontSize: 12 }}>
                <span><code>{h.run_id}</code> · {h.status}</span>
                <span>{h.employees_paid} emp · ${fmt(h.total_debit_usdt)}</span>
                <span style={{ color: "#5F6884" }}>{new Date(h.executed_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </Page>
  );
}

/* ══════════════════ 3. ACQUIRING + SMART TAX + DOCS ══════════════════ */
export function AcquiringPage2() {
  const [overview, setOverview] = useState(null);
  const [terminals, setTerminals] = useState([]);
  const [taxReserves, setTaxReserves] = useState({ buckets: [], total_reserved_usdt: 0 });
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ terminal_type: "pos_physical", label: "", country: "DE", city: "" });
  const [receipt, setReceipt] = useState({ terminal_id: "", amount_usdt: 100 });
  const [docType, setDocType] = useState("invoice");

  const reload = async () => {
    try {
      const [o, t, tr, d] = await Promise.all([
        api.get("/merchant/overview"),
        api.get("/merchant/terminals"),
        api.get("/merchant/tax/reserves"),
        api.get("/merchant/docs/list"),
      ]);
      setOverview(o.data); setTerminals(t.data.terminals || []); setTaxReserves(tr.data); setDocs(d.data.docs || []);
      if (!receipt.terminal_id && t.data.terminals?.[0]) setReceipt((r) => ({ ...r, terminal_id: t.data.terminals[0].terminal_id }));
    } catch (_) { /* noop */ }
  };
  useEffect(() => { reload(); }, []);
  useAutoRefresh(reload, 15000);

  const addTerminal = async () => {
    if (!form.label) return;
    setBusy(true);
    try { await api.post("/merchant/terminals/create", form); setForm({ ...form, label: "" }); await reload(); }
    catch (e) { alert("Create failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const emitReceipt = async () => {
    if (!receipt.terminal_id) { alert("Pick a terminal"); return; }
    setBusy(true);
    try { await api.post("/merchant/receipts/emit", receipt); await reload(); }
    catch (e) { alert("Emit failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const genDoc = async () => {
    setBusy(true);
    try { await api.post("/merchant/docs/generate", { doc_type: docType }); await reload(); }
    catch (e) { alert("Gen failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const iconFor = (t) => t === "pos_physical" ? <Store size={14} /> : t === "online_store" ? <Sparkles size={14} /> : <Landmark size={14} />;

  return (
    <Page title="ACQUIRING & Smart Tax" sub="Multi-terminal · ISO 20022 tax reserve · Auto-documents"
      icon={<Store size={26} />} testId="merchant-acquiring2-page">
      <div className="dash-grid dash-grid-4" style={{ marginBottom: 22 }}>
        <KPI testId="kpi-terminals"  label="Active Terminals" value={terminals.length} color="#00F2FF" icon={<Store size={16} />} sub="POS + online + registers" />
        <KPI testId="kpi-receipts24" label="Receipts (24h)"   value={overview?.acquiring?.receipts_last_24h ?? 0} color="#FFD700" icon={<Receipt size={16} />} sub={`$${fmt(overview?.acquiring?.gross_last_24h_usdt)} gross`} />
        <KPI testId="kpi-gross"      label="Gross Revenue"    value={`$${fmt(overview?.acquiring?.gross_usdt)}`} color="#10F4A7" icon={<Landmark size={16} />} sub="lifetime" />
        <KPI testId="kpi-tax"        label="Tax Reserved"      value={`$${fmt(taxReserves.total_reserved_usdt)}`} color="#BC13FE" icon={<ShieldCheck size={16} />} sub="ISO 20022 · per receipt" />
      </div>

      <div className="dash-grid dash-grid-2" style={{ gap: 20, marginBottom: 20 }}>
        {/* Terminal management */}
        <Card testId="acquiring-terminals-panel">
          <h3 style={{ margin: "0 0 8px", fontSize: 15 }}>Terminals · Add & Manage</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
            <select value={form.terminal_type} onChange={(e) => setForm({ ...form, terminal_type: e.target.value })} data-testid="terminal-type"
              style={{ padding: 10, background: "rgba(0,0,0,0.25)", color: "#E7ECFF", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8 }}>
              <option value="pos_physical">POS Physical</option>
              <option value="online_store">Online Store</option>
              <option value="cash_register">Cash Register</option>
            </select>
            <input placeholder="Label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="terminal-label"
              style={{ padding: 10, background: "rgba(0,0,0,0.25)", color: "#E7ECFF", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8 }} />
            <input placeholder="Country (DE, PL, US…)" maxLength={2} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })} data-testid="terminal-country"
              style={{ padding: 10, background: "rgba(0,0,0,0.25)", color: "#E7ECFF", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8 }} />
            <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
              style={{ padding: 10, background: "rgba(0,0,0,0.25)", color: "#E7ECFF", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8 }} />
          </div>
          <Btn testId="terminal-add-btn" onClick={addTerminal} disabled={busy || !form.label}><Store size={14} /> Add Terminal</Btn>
          <div style={{ marginTop: 14, maxHeight: 180, overflowY: "auto" }}>
            {terminals.length === 0 && <div style={{ fontSize: 12, color: "#5F6884", padding: 12 }}>No terminals yet.</div>}
            {terminals.map((t) => (
              <div key={t.terminal_id} style={{ padding: 10, marginBottom: 8, borderRadius: 8, background: "rgba(0,0,0,0.25)", border: "1px solid rgba(0,242,255,0.15)", display: "flex", justifyContent: "space-between" }}>
                <div>
                  <b style={{ display: "flex", alignItems: "center", gap: 6 }}>{iconFor(t.terminal_type)} {t.label}</b>
                  <div style={{ fontSize: 10, color: "#5F6884" }}>{t.terminal_type} · {t.country} {t.city || ""}</div>
                </div>
                <div style={{ textAlign: "right", fontSize: 11 }}>
                  <div style={{ color: "#10F4A7" }}>{t.throughput_last_24h} tx / 24h</div>
                  <div>${fmt(t.revenue_last_24h_usdt)}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Receipt + Smart Tax */}
        <Card testId="acquiring-receipt-panel">
          <h3 style={{ margin: "0 0 8px", fontSize: 15 }}>Emit Receipt · Smart Tax auto-reserve</h3>
          <select value={receipt.terminal_id} onChange={(e) => setReceipt({ ...receipt, terminal_id: e.target.value })} data-testid="receipt-terminal"
            style={{ width: "100%", padding: 10, marginBottom: 8, background: "rgba(0,0,0,0.25)", color: "#E7ECFF", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8 }}>
            <option value="">— pick terminal —</option>
            {terminals.map((t) => <option key={t.terminal_id} value={t.terminal_id}>{t.label} ({t.country})</option>)}
          </select>
          <input placeholder="Amount USDT" type="number" value={receipt.amount_usdt} onChange={(e) => setReceipt({ ...receipt, amount_usdt: Number(e.target.value) })} data-testid="receipt-amount"
            style={{ width: "100%", padding: 10, marginBottom: 8, background: "rgba(0,0,0,0.25)", color: "#E7ECFF", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8 }} />
          <Btn testId="receipt-emit-btn" onClick={emitReceipt} disabled={busy || !receipt.terminal_id} variant="warning"><Receipt size={14} /> Emit Receipt</Btn>

          <h4 style={{ margin: "16px 0 6px", fontSize: 13, color: "#BC13FE" }}>Tax Reserve Buckets (per month × country)</h4>
          <div style={{ maxHeight: 130, overflowY: "auto" }}>
            {taxReserves.buckets.length === 0 && <div style={{ fontSize: 11, color: "#5F6884" }}>No tax reserves yet.</div>}
            {taxReserves.buckets.map((b, i) => (
              <div key={i} style={{ padding: "6px 8px", display: "flex", justifyContent: "space-between", fontSize: 12, borderBottom: "1px dashed rgba(255,255,255,0.06)" }}>
                <span>{b.month} · {b.country} · rate {(b.rate * 100).toFixed(1)}%</span>
                <span style={{ color: "#BC13FE", fontWeight: 700 }}>${fmt(b.reserved_usdt)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Auto docs */}
      <Card testId="acquiring-docs-panel">
        <h3 style={{ margin: "0 0 8px", fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
          <FileText size={16} color="#FFD700" /> Automated Document Archive — one-click PDF / XLSX
        </h3>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="doc-type"
            style={{ padding: 10, background: "rgba(0,0,0,0.25)", color: "#E7ECFF", border: "1px solid rgba(255,215,0,0.35)", borderRadius: 8 }}>
            <option value="invoice">Invoice</option>
            <option value="balance_sheet">Balance Sheet</option>
            <option value="fiscal_report">Fiscal Report</option>
            <option value="vat_declaration">VAT Declaration</option>
          </select>
          <Btn testId="doc-generate-btn" variant="warning" onClick={genDoc} disabled={busy}><Sparkles size={14} /> Generate</Btn>
        </div>
        <table className="dash-table">
          <thead><tr><th>Doc</th><th>Type</th><th>Receipts</th><th>Gross</th><th>Tax</th><th>Net</th><th>Fingerprint</th></tr></thead>
          <tbody>
            {docs.length === 0 && <tr><td colSpan={7} style={{ textAlign: "center", padding: 20, color: "#5F6884" }}>No documents generated yet.</td></tr>}
            {docs.map((d) => (
              <tr key={d.doc_id} data-testid={`doc-row-${d.doc_id}`}>
                <td><code>{d.doc_id}</code></td>
                <td><b>{d.doc_type}</b></td>
                <td>{d.receipts_count}</td>
                <td>${fmt(d.total_gross_usdt)}</td>
                <td style={{ color: "#BC13FE" }}>${fmt(d.total_tax_reserved_usdt)}</td>
                <td style={{ color: "#10F4A7", fontWeight: 700 }}>${fmt(d.total_net_revenue_usdt)}</td>
                <td style={{ fontSize: 10, color: "#5F6884" }}><code>{d.commitment_sha256}</code></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </Page>
  );
}

/* ══════════════════ 4. SUPPLY CHAIN & B2B ORDERING ══════════════════ */
export function SupplyChainPage() {
  const [overview, setOverview] = useState(null);
  const [catalog, setCatalog] = useState({ items: [], total: 0, low_stock: 0 });
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [lowOnly, setLowOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [form, setForm] = useState({ sku: "", name: "", unit_price_usdt: 0, stock_qty: 0, reorder_threshold: 10, supplier_email: "", supplier_name: "" });
  const fileRef = useRef(null);

  const reload = async () => {
    try {
      const [o, c, p] = await Promise.all([
        api.get("/merchant/overview"),
        api.get(`/merchant/catalog?q=${encodeURIComponent(q)}${lowOnly ? "&low_stock_only=true" : ""}`),
        api.get("/merchant/purchase-orders"),
      ]);
      setOverview(o.data); setCatalog(c.data); setOrders(p.data.purchase_orders || []);
    } catch (_) { /* noop */ }
  };
  useEffect(() => { reload(); }, [q, lowOnly]);
  useAutoRefresh(reload, 20000);

  const doImport = async () => {
    const f = fileRef.current?.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/merchant/catalog/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      alert(`Imported ${r.data.inserted} · updated ${r.data.updated} · skipped ${r.data.skipped}`);
      fileRef.current.value = "";
      await reload();
    } catch (e) { alert("Import failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const doAdd = async () => {
    if (!form.sku || !form.name) { alert("SKU + name required"); return; }
    setBusy(true);
    try { await api.post("/merchant/catalog/add", { ...form, supplier_email: form.supplier_email || null }); setForm({ sku: "", name: "", unit_price_usdt: 0, stock_qty: 0, reorder_threshold: 10, supplier_email: "", supplier_name: "" }); await reload(); }
    catch (e) { alert("Add failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const runScan = async () => {
    setBusy(true);
    try { const r = await api.post("/merchant/inventory/scan"); setScanResult(r.data); await reload(); }
    catch (e) { alert("Scan failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const submitPo = async (po_id) => {
    try { await api.post(`/merchant/purchase-orders/${po_id}/submit`); await reload(); }
    catch (e) { alert("Submit failed: " + (e?.response?.data?.detail || e.message)); }
  };
  const downloadTemplate = () => {
    const csv = "sku,name,unit_price_usdt,stock_qty,reorder_threshold,supplier_email,supplier_name\nSKU-001,Ceramic Mug,4.50,120,25,supplier@a.com,Acme Ltd\nSKU-002,USB-C Cable,3.20,8,20,cables@b.com,Bolt Corp\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "catalog_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Page title="SUPPLY CHAIN & B2B Co-Pilot" sub="Millions-SKU catalog · AI inventory replenishment · Turbo-SWIFT B2B POs"
      icon={<Boxes size={26} />} testId="merchant-supply-page">
      <div className="dash-grid dash-grid-4" style={{ marginBottom: 22 }}>
        <KPI testId="kpi-catalog"    label="Catalog Items"     value={catalog.total ?? 0}     color="#00F2FF" icon={<Boxes size={16} />} sub="SKUs" />
        <KPI testId="kpi-low-stock"  label="Low Stock"          value={catalog.low_stock ?? 0} color={catalog.low_stock > 0 ? "#FF3D6C" : "#10F4A7"} icon={<AlertCircle size={16} />} sub="At/below threshold" />
        <KPI testId="kpi-pos"        label="Purchase Orders"    value={orders.length}          color="#FFD700" icon={<TruckIcon size={16} />} sub={`${orders.filter((o) => o.status === "DRAFT").length} draft`} />
        <KPI testId="kpi-swift-fee"  label="Turbo-SWIFT Fee"    value="0.5%"                   color="#BC13FE" icon={<Zap size={16} />} sub="0.15 s settlement" />
      </div>

      <div className="dash-grid dash-grid-2" style={{ gap: 20, marginBottom: 20 }}>
        {/* Import */}
        <Card testId="supply-import-panel">
          <h3 style={{ margin: "0 0 4px", fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}><UploadCloud size={16} color="#00F2FF" /> Catalog Bulk Import</h3>
          <p style={{ fontSize: 12, color: "#8B94B8", marginTop: 4 }}>Handles millions of SKUs — up to 50 MB CSV.</p>
          <input ref={fileRef} type="file" accept=".csv" data-testid="catalog-file-input"
            style={{ width: "100%", padding: 10, marginBottom: 8, border: "1px dashed rgba(0,242,255,0.35)", borderRadius: 10, background: "rgba(0,242,255,0.03)", color: "#E7ECFF" }} />
          <div style={{ display: "flex", gap: 8 }}>
            <Btn testId="catalog-import-btn" onClick={doImport} disabled={busy}><UploadCloud size={14} /> Import Catalog</Btn>
            <Btn testId="catalog-template-btn" variant="ghost" onClick={downloadTemplate}><FileText size={14} /> CSV Template</Btn>
          </div>
        </Card>

        {/* Single item add */}
        <Card testId="supply-add-panel">
          <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>Add Single SKU</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <input placeholder="SKU" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="sku-sku"
              style={{ padding: 10, background: "rgba(0,0,0,0.25)", color: "#E7ECFF", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8 }} />
            <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sku-name"
              style={{ padding: 10, background: "rgba(0,0,0,0.25)", color: "#E7ECFF", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8 }} />
            <input placeholder="Unit Price USDT" type="number" value={form.unit_price_usdt} onChange={(e) => setForm({ ...form, unit_price_usdt: Number(e.target.value) })}
              style={{ padding: 10, background: "rgba(0,0,0,0.25)", color: "#E7ECFF", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8 }} />
            <input placeholder="Stock Qty" type="number" value={form.stock_qty} onChange={(e) => setForm({ ...form, stock_qty: Number(e.target.value) })} data-testid="sku-stock"
              style={{ padding: 10, background: "rgba(0,0,0,0.25)", color: "#E7ECFF", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8 }} />
            <input placeholder="Reorder threshold" type="number" value={form.reorder_threshold} onChange={(e) => setForm({ ...form, reorder_threshold: Number(e.target.value) })} data-testid="sku-threshold"
              style={{ padding: 10, background: "rgba(0,0,0,0.25)", color: "#E7ECFF", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8 }} />
            <input placeholder="Supplier email" value={form.supplier_email} onChange={(e) => setForm({ ...form, supplier_email: e.target.value })}
              style={{ padding: 10, background: "rgba(0,0,0,0.25)", color: "#E7ECFF", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8 }} />
          </div>
          <Btn testId="sku-add-btn" onClick={doAdd} disabled={busy} style={{ marginTop: 8 }}>Add SKU</Btn>
        </Card>
      </div>

      {/* AI Inventory Replenishment */}
      <Card style={{ marginBottom: 20, background: "linear-gradient(135deg, rgba(0,242,255,0.06), rgba(188,19,254,0.06))", borderColor: "#00F2FF55" }} testId="supply-scan-panel">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}><Cpu size={16} color="#00F2FF" /> AI Inventory Replenishment</h3>
            <div style={{ fontSize: 12, color: "#8B94B8", marginTop: 4 }}>
              Scans catalog for SKUs at/below threshold, drafts B2B Purchase Orders through Turbo-SWIFT B2B (0.5% fee · 0.15 s settlement).
            </div>
          </div>
          <Btn testId="supply-scan-btn" onClick={runScan} disabled={busy}><RefreshCw size={14} /> Run AI Scan</Btn>
        </div>
        {scanResult && (
          <div data-testid="supply-scan-result" style={{ marginTop: 12, padding: 10, borderRadius: 8, background: "rgba(0,0,0,0.28)" }}>
            <b>Drafted {scanResult.drafted} PO(s) · total ${fmt(scanResult.total_cost_usdt)} USDT</b>
          </div>
        )}
      </Card>

      {/* Catalog grid */}
      <Card style={{ marginBottom: 20 }} testId="supply-catalog-panel">
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <input placeholder="Search SKU or name…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="catalog-search"
            style={{ flex: 1, padding: 10, background: "rgba(0,0,0,0.25)", color: "#E7ECFF", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8 }} />
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#8B94B8" }}>
            <input type="checkbox" data-testid="low-stock-only" checked={lowOnly} onChange={(e) => setLowOnly(e.target.checked)} />
            Low stock only
          </label>
        </div>
        <table className="dash-table">
          <thead><tr><th>SKU</th><th>Name</th><th>Price</th><th>Stock</th><th>Threshold</th><th>Supplier</th></tr></thead>
          <tbody>
            {catalog.items.length === 0 && <tr><td colSpan={6} style={{ textAlign: "center", padding: 20, color: "#5F6884" }}>No catalog items yet.</td></tr>}
            {catalog.items.slice(0, 40).map((i) => {
              const low = (i.stock_qty || 0) <= (i.reorder_threshold || 0);
              return (
                <tr key={i.sku} data-testid={`catalog-row-${i.sku}`}>
                  <td><code>{i.sku}</code></td>
                  <td><b>{i.name}</b></td>
                  <td>${fmt(i.unit_price_usdt)}</td>
                  <td style={{ color: low ? "#FF3D6C" : "#10F4A7", fontWeight: 700 }}>{i.stock_qty}</td>
                  <td>{i.reorder_threshold}</td>
                  <td style={{ fontSize: 11, color: "#8B94B8" }}>{i.supplier_name || i.supplier_email || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* Purchase orders */}
      <Card testId="supply-po-panel">
        <h3 style={{ margin: "0 0 8px", fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}><TruckIcon size={16} color="#FFD700" /> B2B Purchase Orders (Turbo-SWIFT)</h3>
        <table className="dash-table">
          <thead><tr><th>PO</th><th>SKU</th><th>Qty</th><th>Total</th><th>Supplier</th><th>Status</th><th style={{ textAlign: "right" }}>Actions</th></tr></thead>
          <tbody>
            {orders.length === 0 && <tr><td colSpan={7} style={{ textAlign: "center", padding: 20, color: "#5F6884" }}>No purchase orders yet — run the AI Scan above.</td></tr>}
            {orders.map((o) => (
              <tr key={o.po_id} data-testid={`po-row-${o.po_id}`}>
                <td><code>{o.po_id}</code></td>
                <td><b>{o.sku}</b><div style={{ fontSize: 10, color: "#5F6884" }}>{o.item_name}</div></td>
                <td>{o.quantity}</td>
                <td style={{ fontWeight: 700 }}>${fmt(o.total_cost_usdt)}</td>
                <td style={{ fontSize: 11 }}>{o.supplier_name || o.supplier_email || "—"}</td>
                <td>
                  <span style={{ padding: "2px 8px", borderRadius: 999, fontSize: 10, background: o.status === "SUBMITTED" ? "#10F4A733" : "#FFD70033", color: o.status === "SUBMITTED" ? "#10F4A7" : "#FFD700" }}>
                    {o.status}
                  </span>
                </td>
                <td style={{ textAlign: "right" }}>
                  {o.status === "DRAFT" && <Btn variant="warning" onClick={() => submitPo(o.po_id)} testId={`po-submit-${o.po_id}`}><Send size={12} /> Submit</Btn>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </Page>
  );
}

/* ══════════════════ 0. OVERVIEW HUB ══════════════════ */
export function MerchantOverviewPage() {
  const [o, setO] = useState(null);
  const reload = async () => {
    try { const r = await api.get("/merchant/overview"); setO(r.data); } catch (_) { /* noop */ }
  };
  useEffect(() => { reload(); }, []);
  useAutoRefresh(reload, 15000);

  const compliance = o?.compliance || {};
  const compliancePct = compliance.agreements_required
    ? Math.round((compliance.agreements_locked || 0) / compliance.agreements_required * 100) : 0;

  return (
    <Page title="MERCHANT 2.0 — AI Command Center"
      sub={`Tier: ${o?.merchant?.account_type || "—"} · ${o?.merchant?.is_enterprise ? "Enterprise unlocked" : "Retail plan"}`}
      icon={<Building2 size={26} />}
      testId="merchant-overview-page">
      <FiatStabilityBanner />
      <div className="dash-grid dash-grid-4" style={{ marginBottom: 22 }}>
        <KPI testId="ovw-compliance" label="Compliance"     value={`${compliancePct}%`}                                  color={compliancePct === 100 ? "#10F4A7" : "#FFD700"} icon={<ShieldCheck size={16} />} sub={`${compliance.agreements_locked || 0}/${compliance.agreements_required || 4} agreements locked`} />
        <KPI testId="ovw-headcount"  label="Payroll Headcount" value={o?.payroll?.headcount ?? "—"}                      color="#00F2FF" icon={<Users size={16} />} sub={`${o?.payroll?.runs_executed || 0} runs executed`} />
        <KPI testId="ovw-terminals"  label="Terminals"      value={o?.acquiring?.terminals ?? "—"}                        color="#BC13FE" icon={<Store size={16} />} sub={`${o?.acquiring?.receipts_last_24h ?? 0} receipts (24h)`} />
        <KPI testId="ovw-catalog"    label="SKUs"           value={o?.supply?.catalog_items ?? "—"}                       color="#FFD700" icon={<Boxes size={16} />} sub={`${o?.supply?.low_stock || 0} low stock · ${o?.supply?.purchase_orders || 0} POs`} />
      </div>

      <div className="dash-grid dash-grid-2" style={{ gap: 20 }}>
        <Card style={{ background: "linear-gradient(135deg, rgba(255,215,0,0.12), rgba(255,159,67,0.05))", borderColor: "#FFD70055" }}>
          <h3 style={{ margin: "0 0 4px", fontSize: 15, color: "#FFD700" }}>Hybrid Corporate Banking · STEP 14</h3>
          <p style={{ fontSize: 12, color: "#8B94B8" }}>Multi-currency corporate account + TGC Snowball ledger with Fiat Stability Lock.</p>
          <a href="/dashboard/merchant/banking" style={{ color: "#FFD700", textDecoration: "none", fontSize: 12, fontWeight: 700 }}>Open module →</a>
        </Card>
        <Card style={{ background: "linear-gradient(135deg, rgba(16,244,167,0.10), rgba(0,242,255,0.05))", borderColor: "#10F4A755" }}>
          <h3 style={{ margin: "0 0 4px", fontSize: 15, color: "#10F4A7" }}>Zero-Legal AI Compliance</h3>
          <p style={{ fontSize: 12, color: "#8B94B8" }}>Encrypted KYB vault + AI Corporate Lawyer + Web3 agreement lock.</p>
          <a href="/dashboard/merchant/legal" style={{ color: "#00F2FF", textDecoration: "none", fontSize: 12, fontWeight: 700 }}>Open module →</a>
        </Card>
        <Card style={{ background: "linear-gradient(135deg, rgba(188,19,254,0.10), rgba(255,215,0,0.05))", borderColor: "#BC13FE55" }}>
          <h3 style={{ margin: "0 0 4px", fontSize: 15, color: "#BC13FE" }}>Turbo-Zarplata Payroll</h3>
          <p style={{ fontSize: 12, color: "#8B94B8" }}>10 000+ roster · RBAC · bulk one-click payout to Cash Wallets.</p>
          <a href="/dashboard/merchant/payroll" style={{ color: "#BC13FE", textDecoration: "none", fontSize: 12, fontWeight: 700 }}>Open module →</a>
        </Card>
        <Card style={{ background: "linear-gradient(135deg, rgba(0,242,255,0.10), rgba(255,215,0,0.06))", borderColor: "#00F2FF55" }}>
          <h3 style={{ margin: "0 0 4px", fontSize: 15, color: "#00F2FF" }}>Acquiring & Smart Tax</h3>
          <p style={{ fontSize: 12, color: "#8B94B8" }}>Multi-terminal + ISO-20022 auto-reserve + PDF/XLSX docs.</p>
          <a href="/dashboard/merchant/acquiring" style={{ color: "#00F2FF", textDecoration: "none", fontSize: 12, fontWeight: 700 }}>Open module →</a>
        </Card>
        <Card style={{ background: "linear-gradient(135deg, rgba(255,215,0,0.10), rgba(16,244,167,0.05))", borderColor: "#FFD70055" }}>
          <h3 style={{ margin: "0 0 4px", fontSize: 15, color: "#FFD700" }}>Supply Chain Co-Pilot</h3>
          <p style={{ fontSize: 12, color: "#8B94B8" }}>Millions-SKU catalog · AI reorder scan · Turbo-SWIFT B2B POs.</p>
          <a href="/dashboard/merchant/supply" style={{ color: "#FFD700", textDecoration: "none", fontSize: 12, fontWeight: 700 }}>Open module →</a>
        </Card>
      </div>
    </Page>
  );
}
